import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Check, EllipsisVertical, Pen, Pencil, Trash2, UserPlus } from 'lucide-react'
import { useState } from 'react'

import { type AdminStaffMember, formatName, STAFF_ROLES } from '@snootsvet/shared'

import { Badge } from '@/frontend/components/ui/badge'
import { Button } from '@/frontend/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/frontend/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/frontend/components/ui/dropdown-menu'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/frontend/components/ui/table'
import { StaffForm } from '@/frontend/features/admin/staff.form'
import { StaffEsignatureDialog } from '@/frontend/features/admin/staff-esignature-dialog'
import { useListAdminStaff } from '@/frontend/hooks/api/use-list-admin-staff'
import { useOsApi } from '@/frontend/hooks/api/use-os-api'
import { useDocumentTitle } from '@/frontend/hooks/use-document-title'

function AddStaffDialog({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Staff Member</DialogTitle>
        </DialogHeader>
        <StaffForm onSuccess={() => onOpenChange(false)} onCancel={() => onOpenChange(false)} />
      </DialogContent>
    </Dialog>
  )
}

function EditStaffDialog({
  member,
  open,
  onOpenChange,
}: {
  member: AdminStaffMember
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Staff Member</DialogTitle>
        </DialogHeader>
        <StaffForm
          staffId={member.id}
          defaultValues={{
            email: member.email,
            firstName: member.firstName,
            lastName: member.lastName,
            role: member.role,
            isSuperuser: member.isSuperuser,
            licenseNumber: member.licenseNumber,
            regions: member.regions,
            bookingVisibility: member.bookingVisibility,
          }}
          onSuccess={() => onOpenChange(false)}
          onCancel={() => onOpenChange(false)}
        />
      </DialogContent>
    </Dialog>
  )
}

function StaffTable({ staff }: { staff: AdminStaffMember[] }) {
  const api = useOsApi()
  const queryClient = useQueryClient()

  const [staffToEdit, setStaffToEdit] = useState<AdminStaffMember | null>(null)
  const [esignatureStaffId, setEsignatureStaffId] = useState<string | null>(null)
  const [staffToDelete, setStaffToDelete] = useState<AdminStaffMember | null>(null)

  const deleteQuery = useMutation({
    mutationFn: async (id: string) => {
      const response = await api.delete('/admin/staff/:id', { params: { id } })
      if (!response.success) throw new Error(response.error.message)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'staff'] })
      queryClient.invalidateQueries({ queryKey: ['staff'] })
      setStaffToDelete(null)
    },
  })

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Email</TableHead>
            <TableHead>Name</TableHead>
            <TableHead className="text-center">Role</TableHead>
            {/* Region-neutral: the column shows each vet's region-specific license number. */}
            <TableHead className="text-center">License</TableHead>
            <TableHead className="text-center">E-Sig</TableHead>
            <TableHead className="text-center">Superuser</TableHead>
            <TableHead className="text-center">Clerk</TableHead>
            <TableHead></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {staff.map((member) => (
            <TableRow key={member.id}>
              <TableCell>{member.email}</TableCell>
              <TableCell>{formatName(member) || '—'}</TableCell>
              <TableCell className="text-center">
                {STAFF_ROLES.find((r) => r.value === member.role)?.label ?? member.role}
              </TableCell>
              <TableCell className="text-center">
                <span className="text-sm text-muted-foreground">{member.licenseNumber || '—'}</span>
              </TableCell>
              <TableCell className="text-center">
                {member.hasEsignature && <Check className="mx-auto size-4 text-success-fg" />}
              </TableCell>
              <TableCell className="text-center">
                {member.isSuperuser && (
                  <Check aria-label="Superuser" className="mx-auto size-4 text-success-fg" />
                )}
              </TableCell>
              <TableCell>
                <div className="text-center">
                  {member.clerkUserId ? (
                    <Badge variant="secondary">Linked</Badge>
                  ) : (
                    <span className="text-muted-foreground text-sm">Pending invite</span>
                  )}
                </div>
              </TableCell>
              <TableCell>
                <div className="flex items-center justify-center gap-1">
                  <Button variant="ghost" size="icon" onClick={() => setStaffToEdit(member)}>
                    <Pencil className="size-3.5" />
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger render={<Button variant="ghost" size="icon" />}>
                      <EllipsisVertical className="size-3.5" />
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {(member.role === 'vet' || member.role === 'remote_vet') && (
                        <DropdownMenuItem onClick={() => setEsignatureStaffId(member.id)}>
                          <Pen className="size-3.5" />
                          E-Signature
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem
                        className="text-destructive-fg"
                        onClick={() => setStaffToDelete(member)}
                      >
                        <Trash2 className="size-3.5" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {esignatureStaffId && (
        <StaffEsignatureDialog
          staffId={esignatureStaffId}
          open={true}
          onOpenChange={(open) => {
            if (!open) setEsignatureStaffId(null)
          }}
        />
      )}

      {staffToEdit && (
        <EditStaffDialog
          member={staffToEdit}
          open={true}
          onOpenChange={(open) => {
            if (!open) setStaffToEdit(null)
          }}
        />
      )}

      <Dialog
        open={staffToDelete !== null}
        onOpenChange={(open) => {
          if (!open) setStaffToDelete(null)
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove staff member?</DialogTitle>
          </DialogHeader>
          <p className="text-muted-foreground">
            {staffToDelete && (
              <>
                <span className="font-medium text-foreground">
                  {[staffToDelete.firstName, staffToDelete.lastName].filter(Boolean).join(' ') ||
                    staffToDelete.email}
                </span>{' '}
                will be removed from the system. This cannot be undone.
              </>
            )}
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStaffToDelete(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={deleteQuery.isPending}
              onClick={() => staffToDelete && deleteQuery.mutate(staffToDelete.id)}
            >
              {deleteQuery.isPending ? 'Removing...' : 'Remove'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

export function StaffAdminPage() {
  useDocumentTitle('Staff')
  const [dialogOpen, setDialogOpen] = useState(false)
  const { data, isLoading, error } = useListAdminStaff()

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-headline-lg">Staff</h1>
        <Button size="lg" onClick={() => setDialogOpen(true)}>
          <UserPlus />
          Add Staff Member
        </Button>
      </div>

      {isLoading ? (
        <p className="text-muted-foreground">Loading...</p>
      ) : error ? (
        <p className="text-destructive-fg">Error: {error.message}</p>
      ) : data?.length === 0 ? (
        <p className="text-muted-foreground">No staff members yet.</p>
      ) : (
        <StaffTable staff={data ?? []} />
      )}

      <AddStaffDialog open={dialogOpen} onOpenChange={setDialogOpen} />
    </div>
  )
}
